<template>
  <div class="w-full" tabindex="0">
    <div v-if="label.length > 0" class="flex">
      <label v-if="label.length > 0" class="text-xs tracking-wide uppercase label" :class="{ required }" v-text="label" />
    </div>

    <div
      ref="refSelect" class="block w-full min-h-9"
      :class="{ 'bg-gray-200 rounded': disabled, 'border-red-300 bg-red-50 placeholder-red-200 text-red-900': hasError, 'bg-white': !disabled }"
    >
      <div
        class="cursor-pointer flex relative items-center w-full min-h-9 h-full mt-0 px-0.5 border border-form rounded shadow-input focus:border-primary text-base text-default"
        aria-haspopup="listbox" aria-expanded="true" aria-labelledby="listbox-label" @click="doOpenDropdown"
      >
        <div v-if="$slots.prefix" class="w-5 shrink-0">
          <slot name="prefix" />
        </div>
        <template v-if="hasValue">
          <div class="pl-2 truncate grow" v-text="displayModelValue" />
        </template>
        <div v-else class="truncate grow text-grey" v-text="placeholder" />
        <!-- </div> -->
        <div class="inset-y-0 right-0 flex items-center justify-end w-4 h-full mr-2 pointer-events-none shrink-0 grow">
          <font-awesome-icon icon="fa-light fa-chevron-down" />
        </div>
      </div>
    </div>
    <div v-if="hasError">
      <p v-for="error in errors" :key="error.$uid" class="text-xs italic text-red-500 overflow-auto ">
        {{ error.$message }}
      </p>
    </div>
    <div
      v-show="isOpen" ref="refDropdown"
      class="max-w-lg py-1 overflow-x-hidden text-base bg-white shadow-dropdown max-h-60 overflow-y-overlay z-dropdown focus:outline-none"
    >
      <div v-if="clearable" class="flex justify-between py-2 pl-3 pr-3">
        <tx-button v-if="clearable" type="text" :text="t('general.reset')" @click="doReset()" />
      </div>
      <tx-input
        v-if="filterable" ref="refFilter" v-model="filterText" clearable faicon="fa-light fa-magnifying-glass" class="pb-1 mx-3"
        :placeholder="t('general.search')"
      />
      <ul tabindex="-1" role="listbox" aria-labelledby="listbox-label" aria-activedescendant="listbox-item-3" class="leading-9 grid grid-cols-2 md:grid-cols-3 gap-4 p-4">
        <li
          v-for="(d, index) in filteredData" :id="`listbox-item-${index}`" :key="hasValueProp ? d[valueProp] : d"
          role="option" class="relative"
        >
          <div
            class="flex flex-col items-center p-2 text-gray-900 rounded select-none hover:bg-gray-100" :class="{ 'ring-2 ring-primary-500': isSelected(d) }"
            @click="doSelect(hasValueProp ? d[valueProp] : d)"
          >
            <div class="relative w-full aspect-video overflow-hidden rounded-sm mb-2">
              <img
                v-if="d.layoutImage"
                :src="d.layoutImage"
                alt="Thumbnail"
                class="absolute inset-0 w-full h-full object-contain"
              >
            </div>
            <div class="text-center truncate w-full" :class="{ 'text-primary-500': isSelected(d) }">
              {{ hasDisplayProp ? d[displayProp] : d }}
            </div>
          </div>
        </li>
        <li v-if="filteredData.length === 0" class="col-span-2 md:col-span-3">
          <div class="flex items-center justify-center space-x-3">
            <span class="block italic truncate text-dark-grey" v-text="t('general.emptyList')" />
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isNull, isUndefined } from 'lodash-es'
import { useI18n } from 'vue-i18n'
import type { Instance } from '@popperjs/core'
import { createPopper } from '@popperjs/core'
import { onClickOutside } from '@vueuse/core'
import type { ErrorObject } from '@vuelidate/core'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import TxButton from './TxButton.vue'
import TxInput from './TxInput.vue'
import { sameWidthModifier } from '@/services/utils'

interface IProps {
  label?: string
  errors?: ErrorObject[]
  data?: any[]
  modelValue?: any
  valueProp?: string
  displayProp?: string
  placeholder?: string
  filterable?: boolean
  clearable?: boolean
  disabled?: boolean
  required?: boolean
  sortList?: boolean
}
const props = withDefaults(defineProps<IProps>(), { label: '', errors: () => [] as ErrorObject[], data: () => [] as any[], modelValue: null, valueProp: '', displayProp: '', placeholder: '', filterable: false, clearable: false, disabled: false, required: false, sortList: true })

const emit = defineEmits<{
  (e: 'update:modelValue', val: any): void
  (e: 'change', val: any): void
}>()

const { t } = useI18n()
const refSelect = ref()
const refDropdown = ref()
const isOpen = ref(false)
const filterText = ref('')
const refFilter = ref<HTMLInputElement>()
let popper: Instance

const hasError = computed(() => props.errors.length)

const hasDisplayProp = computed(() => props.displayProp.length > 0)

const hasValueProp = computed(() => props.valueProp.length > 0)

const hasValue = computed(() => !isUndefined(props.modelValue) && !isNull(props.modelValue) && props.modelValue !== '')

const displayModelValue = computed(() => {
  if (hasValue.value) {
    let found: any = null

    const findItem = (itm: any) => {
      if (hasValueProp.value) {
        return itm[props.valueProp]?.toString().toLowerCase() === props.modelValue.toString().toLowerCase()
      }
      else {
        return itm.toString().toLowerCase() === props.modelValue.toString().toLowerCase()
      }
    }

    for (const parent of props.data) {
      if (findItem(parent)) {
        found = parent
        break
      }
      if (found) { break }
    }

    if (!found) {
      if (props.required) {
        notifyUpdate('') // call notifyUpdate method to emit the change event to set the value as empty string
      }
      return ''
    }

    return hasDisplayProp.value ? found[props.displayProp] : found
  }
  return ''
})

const sortedData = computed(() => {
  const propsData = props.data
  if (props.sortList) {
    return [...propsData].sort((a, b) => {
      const aVal = hasDisplayProp.value ? a[props.displayProp]?.toString().trim().toLowerCase() : a?.toString().trim().toLowerCase()
      const bVal = hasDisplayProp.value ? b[props.displayProp]?.toString().trim().toLowerCase() : b?.toString().trim().toLowerCase()
      if (aVal < bVal) {
        return -1
      }
      else if (aVal > bVal) {
        return 1
      }
      else {
        return 0
      }
    })
  }
  else {
    return propsData
  }
})

const filteredData = computed(() => {
  if (filterText.value.length === 0) { return sortedData.value }
  const filterTextLowercase = filterText.value.toLowerCase()
  return sortedData.value.reduce((acc, itm) => {
    const isParentMatch = hasDisplayProp.value ? itm[props.displayProp].toString().toLowerCase().includes(filterTextLowercase) : itm.toString().toLowerCase().includes(filterTextLowercase)

    if (itm.children) {
      const children = itm.children?.filter((child) => {
        return hasDisplayProp.value ? child[props.displayProp].toString().toLowerCase().includes(filterTextLowercase) : child.toString().toLowerCase().includes(filterTextLowercase)
      })

      if ((children && children.length > 0)) {
        acc.push({ ...itm, children })
      }
    }
    else if (isParentMatch) {
      acc.push(itm)
    }

    return acc
  }, [])
})

function isSelected(val) {
  const v = hasValueProp.value ? val[props.valueProp] : val
  return props.modelValue === v
}

function doCloseDropdown() {
  isOpen.value = false
}

function doOpenDropdown() {
  if (!props.disabled) {
    isOpen.value = true
    popper.update()
    if (props.filterable) {
      nextTick(() => {
        refFilter.value?.focus()
      })
    }
  }
}

function doSelect(keyVal) {
  const newVal = keyVal
  isOpen.value = false
  notifyUpdate(newVal)

  nextTick(() => {
    popper.update()
  })
}

function doReset() {
  let newVal
  nextTick(() => {
    popper.update()
  })
  notifyUpdate(newVal)
}

onMounted(() => {
  popper = createPopper(refSelect.value, refDropdown.value, {
    placement: 'bottom-start',
    modifiers: [
      sameWidthModifier,
      {
        name: 'offset',
        options: {
          offset: [0, 1],
        },
      },
    ],
    strategy: 'fixed',
  })

  onClickOutside(refDropdown.value, () => {
    doCloseDropdown()
  })
})

onUnmounted(() => {
  if (popper) {
    popper.destroy()
  }
})

function notifyUpdate(newVal: any) {
  emit('update:modelValue', newVal)
  emit('change', newVal)
}

function focus() {
  refSelect.value?.focus()
}

defineExpose({
  focus,
  openDropdown: doOpenDropdown,
})
</script>
